import { CartAddEvent } from '@theme/events';

const money = (cents, format) => {
  if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
  return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'EUR',
  }).format(Number(cents || 0) / 100);
};

class HairahLookExperience extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.bindGallery();
    this.querySelectorAll('[data-look]').forEach((look) => this.bindLook(look));
  }

  bindGallery() {
    const more = this.querySelector('[data-show-more-looks]');
    more?.addEventListener('click', () => {
      const hidden = [...this.querySelectorAll('[data-look-card][hidden]')];
      hidden.forEach((card, index) => {
        card.hidden = false;
        card.style.setProperty('--reveal-delay', `${Math.min(index, 5) * 70}ms`);
        card.classList.add('is-revealing');
      });
      more.hidden = true;
      more.setAttribute('aria-expanded', 'true');
      hidden[0]?.focus({ preventScroll: true });
    });

    this.querySelectorAll('[data-open-look]').forEach((button) => {
      button.addEventListener('click', () => document.getElementById(button.dataset.openLook)?.showModal());
    });
    this.querySelectorAll('[data-close-look]').forEach((button) => {
      button.addEventListener('click', () => button.closest('dialog')?.close());
    });
    this.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });
  }

  bindLook(look) {
    const total = look.querySelector('[data-total]');
    const button = look.querySelector('[data-add]');
    const message = look.querySelector('[data-message]');
    const update = () => {
      let sum = 0;
      look.querySelectorAll('[data-item]').forEach((item) => {
        const check = item.querySelector('[data-check]');
        const select = item.querySelector('[data-variant]');
        const price = Number(select.options[select.selectedIndex]?.dataset.price || 0);
        if (check.checked) sum += price;
        item.querySelector('[data-price]').textContent = money(price, this.moneyFormat);
        item.classList.toggle('is-excluded', !check.checked);
      });
      total.textContent = money(sum, this.moneyFormat);
      button.disabled = !look.querySelector('[data-check]:checked');
    };
    look.addEventListener('change', update);
    update();

    button.addEventListener('click', async () => {
      const items = [...look.querySelectorAll('[data-item]')]
        .filter((item) => item.querySelector('[data-check]').checked)
        .map((item) => ({ id: Number(item.querySelector('[data-variant]').value), quantity: 1 }));
      if (!items.length) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      message.textContent = this.dataset.adding;

      try {
        const sectionIds = [...new Set(
          [...document.querySelectorAll('cart-items-component[data-section-id]')]
            .map((element) => element.dataset.sectionId)
            .filter(Boolean),
        )];
        const response = await fetch(window.Theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds, sections_url: window.location.pathname }),
        });
        const added = await response.json();
        if (!response.ok || added.status) throw new Error(added.description || added.message || this.dataset.addError);

        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        const cart = await cartResponse.json();
        if (!cartResponse.ok) throw new Error(this.dataset.cartReadError);

        this.dispatchEvent(new CartAddEvent(cart, String(items[0].id), {
          source: 'product-form-component',
          itemCount: items.length,
          variantId: String(items[0].id),
          sections: added.sections,
        }));
        look.closest('dialog[open]')?.close();
        message.textContent = this.dataset.added;
      } catch (error) {
        message.textContent = error?.message || this.dataset.updateError;
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        update();
      }
    });
  }
}

if (!customElements.get('hairah-look-experience')) {
  customElements.define('hairah-look-experience', HairahLookExperience);
}
